import React from 'react';
import { collect } from 'collect.js';
import { useTranslation } from 'react-i18next';
import HelperMethods from '../../../../services/exports/HelperMethods';
import useHelpers from '../../../../hooks/context/useHelpers';
import useStatistics from '../../../../hooks/data/useStatistics';
import Metric from '../../../../components/stats/Metric';
import MetricsContainer from '../../../../components/stats/MetricsContainer';
import StatisticsProvider from '../../StatisticsProvider';
import MathHelpers from '../../../../services/helpers/MathHelpers';
import useServiceProvider from '../../../../hooks/data/useServiceProvider';
import {
  PLG_HIDDEN_RESTAURANT_DETAILS_FIELDS,
  PLG_HIDDEN_RESTAURANT_DETAILS_METRICS,
  excludePlgHiddenFields,
  isPlgHiddenMetric,
} from '../../../../services/tenant/plgOnboarding';

const salesThresholds = [0, 300, 1000, 2000, 3500];

function Metrics() {
  const { t: tConstants } = useTranslation(undefined, { keyPrefix: 'Shared:Constants' });

  const { formatCurrency } = useHelpers();
  const { stats, loadingMain, loadingReservations, getMetricLabel } = useStatistics();
  // Pricing adoption, mobile-app and storefront breakdowns are FoodAmigos
  // commercial metrics — hidden for self-serve (PLG) partners.
  const { hasPlgOnboarding } = useServiceProvider();

  const percentageFormatter = (value) => HelperMethods.formatPercentage(value);

  const metrics = stats
    ? [
        <Metric
          key={getMetricLabel('restaurant_partners')}
          name={getMetricLabel('restaurant_partners')}
          loading={loadingMain}
          mainMetrics={['companies', 'signed_companies', 'live_companies', 'live_companies_percentage']}
          excludedHistoryMetrics={['live_companies_percentage']}
          items={[
            {
              name: getMetricLabel('registered_restaurants'),
              ...stats.companies,
            },
            {
              name: getMetricLabel('signed_restaurants'),
              ...stats.signed_companies,
            },
            {
              name: getMetricLabel('live_companies'),
              ...stats.live_companies,
            },
            {
              name: getMetricLabel('live_companies_percentage'),
              formatter: percentageFormatter,
              ...stats.live_companies_percentage,
            },
          ]}
        />,
        <Metric
          key={getMetricLabel('setup_packages_breakdown')}
          name={getMetricLabel('setup_packages_breakdown')}
          loading={loadingMain}
          mainMetrics={['setup_packages_breakdown']}
          excludedHistoryMetrics={['*']}
          items={collect(stats.setup_packages_breakdown?.value)
            .map((item) => {
              const previous =
                collect(stats.setup_packages_breakdown.previous).firstWhere('name', item.name)?.value ?? 0;
              const name = item.stripe_billing_price
                ? `${item.name} (${formatCurrency(item.stripe_billing_price)} / ${formatCurrency(item.internal_invoice_billing_price)})`
                : item.name;

              return {
                name,
                value: item.value,
                previous: previous === 0 ? 0 : previous,
                change: MathHelpers.percentageDiff(item.value, previous),
              };
            })
            .toArray()}
        />,
        <Metric
          key={getMetricLabel('setup_packages_billing_mode_breakdown')}
          name={getMetricLabel('setup_packages_billing_mode_breakdown')}
          loading={loadingMain}
          mainMetrics={['setup_packages_billing_mode_breakdown']}
          excludedHistoryMetrics={['*']}
          items={collect(stats.setup_packages_billing_mode_breakdown?.value)
            .map((item) => {
              const previous =
                collect(stats.setup_packages_billing_mode_breakdown.previous).first(
                  (i) => i.name === item.name && i.billing_mode === item.billing_mode,
                )?.value ?? 0;

              const billingModeName = tConstants(`billing_modes.${item.billing_mode}`);
              const name = item.price ? `${item.name} (${billingModeName} - ${formatCurrency(item.price)})` : item.name;

              return {
                name,
                value: item.value,
                previous: previous === 0 ? 0 : previous,
                change: MathHelpers.percentageDiff(item.value, previous),
              };
            })
            .toArray()}
        />,
        <Metric
          key={getMetricLabel('crossed_sales', { value: formatCurrency(1000) })}
          name={getMetricLabel('crossed_sales', { value: formatCurrency(1000) })}
          loading={loadingMain}
          mainMetrics={[
            'crossed_sales_level_this_month',
            'crossed_sales_level_total',
            'crossed_sales_level_percentage',
          ]}
          excludedHistoryMetrics={['*']}
          isStatic
          items={[
            {
              name: getMetricLabel('crossed_sales_level'),
              value: stats.crossed_sales_level_this_month,
            },
            {
              name: getMetricLabel('crossed_sales_level_total'),
              value: stats.crossed_sales_level_total,
            },
            {
              name: getMetricLabel('crossed_sales_level_percentage'),
              formatter: percentageFormatter,
              value: stats.crossed_sales_level_percentage,
            },
          ]}
        />,
        <Metric
          key={getMetricLabel('sales_tiers')}
          name={getMetricLabel('sales_tiers')}
          loading={loadingMain}
          mainMetrics={salesThresholds.map((item) => `crossed_monthly_sales_threshold_${item}`)}
          excludedHistoryMetrics={['*']}
          items={salesThresholds.map((item, index) => ({
            name: index === 0 ? `${item}-${formatCurrency(salesThresholds[index + 1])}` : `+ ${formatCurrency(item)}`,
            ...stats[`crossed_monthly_sales_threshold_${item}`],
          }))}
        />,
        !isPlgHiddenMetric('new_pricing_adoption_rate', PLG_HIDDEN_RESTAURANT_DETAILS_METRICS, hasPlgOnboarding) && (
          <Metric
            key={getMetricLabel('new_pricing_adoption_rate')}
            name={getMetricLabel('new_pricing_adoption_rate')}
            loading={loadingMain}
            mainMetrics={salesThresholds.map((item) => `crossed_monthly_sales_threshold_${item}`)}
            excludedHistoryMetrics={['*']}
            isStatic
            items={{
              value: `${stats.new_generation_plan_subscriptions_count?.value} / ${stats.all_plan_subscriptions_count?.value} (${stats.new_generation_plan_adoption_rate?.value}%)`,
              previous: `${stats.mobile_app_subscriptions_count?.previous} / ${stats.all_plan_subscriptions_count?.previous} (${stats.new_generation_plan_adoption_rate?.previous}%)`,
            }}
          />
        ),
        !isPlgHiddenMetric('mobile_app_attach_rate', PLG_HIDDEN_RESTAURANT_DETAILS_METRICS, hasPlgOnboarding) && (
          <Metric
            key={getMetricLabel('mobile_app_attach_rate')}
            name={getMetricLabel('mobile_app_attach_rate')}
            loading={loadingMain}
            mainMetrics={['mobile_app_subscriptions_count', 'companies', 'mobile_app_attach_rate']}
            excludedHistoryMetrics={['*']}
            items={{
              value: `${stats.mobile_app_subscriptions_count?.value} / ${stats.companies?.value} (${stats.mobile_app_attach_rate?.value}%)`,
              previous: `${stats.mobile_app_subscriptions_count?.previous} / ${stats.companies?.previous} (${stats.mobile_app_attach_rate?.previous}%)`,
            }}
          />
        ),
        !isPlgHiddenMetric(
          'mobile_app_setup_fee_breakdown',
          PLG_HIDDEN_RESTAURANT_DETAILS_METRICS,
          hasPlgOnboarding,
        ) && (
          <Metric
            key={getMetricLabel('mobile_app_setup_fee_breakdown')}
            name={getMetricLabel('mobile_app_setup_fee_breakdown')}
            loading={loadingMain}
            mainMetrics={['mobile_app_setup_fee_breakdown']}
            excludedHistoryMetrics={['*']}
            items={collect(stats.mobile_app_setup_fee_breakdown?.value)
              .map((item) => {
                const previous =
                  collect(stats.mobile_app_setup_fee_breakdown.previous).first(
                    (i) => i.mobile_app_setup_fee === item.mobile_app_setup_fee,
                  )?.value ?? 0;

                return {
                  name: item.mobile_app_setup_fee,
                  value: item.value,
                  previous: previous === 0 ? 0 : previous,
                  change: MathHelpers.percentageDiff(item.value, previous),
                };
              })
              .toArray()}
          />
        ),
        <Metric
          key={getMetricLabel('order_method')}
          name={getMetricLabel('order_method')}
          loading={loadingMain}
          mainMetrics={['has_delivery', 'has_only_pickup', 'use_external_delivery']}
          items={[
            {
              name: getMetricLabel('own_delivery'),
              ...stats.has_delivery,
            },
            {
              name: getMetricLabel('only_pickup'),
              ...stats.has_only_pickup,
            },
            {
              name: getMetricLabel('use_external_delivery'),
              ...stats.use_external_delivery,
            },
          ]}
        />,
        !isPlgHiddenMetric('storefront', PLG_HIDDEN_RESTAURANT_DETAILS_METRICS, hasPlgOnboarding) && (
          <Metric
            key={getMetricLabel('storefront')}
            name={getMetricLabel('storefront')}
            loading={loadingMain}
            mainMetrics={['has_storefront', 'has_storefront_percentage']}
            excludedHistoryMetrics={['has_storefront_percentage']}
            items={[
              {
                name: getMetricLabel('has_storefront'),
                ...stats.has_storefront,
              },
              {
                name: getMetricLabel('has_storefront_percentage'),
                formatter: percentageFormatter,
                ...stats.has_storefront_percentage,
              },
            ]}
          />
        ),
        <Metric
          key={getMetricLabel('use_reservations')}
          name={getMetricLabel('use_reservations')}
          loading={loadingReservations}
          reservationMetrics={['companies_with_activated_reservations']}
          items={[
            {
              name: getMetricLabel('number'),
              ...stats.companies_with_activated_reservations,
            },
            {
              name: getMetricLabel('restaurants_percentage'),
              formatter: percentageFormatter,
              value: HelperMethods.percentagePart(
                stats.companies_with_activated_reservations?.value,
                stats.companies?.value,
              ),
              previous: HelperMethods.percentagePart(
                stats.companies_with_activated_reservations?.previous,
                stats.companies?.previous,
              ),
            },
          ]}
        />,
      ].filter(Boolean)
    : [];

  return <MetricsContainer metrics={metrics} />;
}

export default function RestaurantDetails({ dateRangeFilter, renderDateRangeFilter }) {
  const { hasPlgOnboarding } = useServiceProvider();

  return (
    <StatisticsProvider
      dateRangeFilter={dateRangeFilter}
      mainMetrics={excludePlgHiddenFields(
        [
          'companies',
          'signed_companies',
          'live_companies',
          'live_companies_percentage',
          'setup_packages_breakdown',
          'setup_packages_billing_mode_breakdown',
          'crossed_sales_level_this_month',
          'crossed_sales_level_total',
          'crossed_sales_level_percentage',
          'has_delivery',
          'has_only_pickup',
          'use_external_delivery',
          'has_storefront',
          'has_storefront_percentage',
          ...salesThresholds.map((item) => `crossed_monthly_sales_threshold_${item}`),
          'new_generation_plan_subscriptions_count',
          'all_plan_subscriptions_count',
          'new_generation_plan_adoption_rate',
          'mobile_app_subscriptions_count',
          'mobile_app_attach_rate',
          'mobile_app_setup_fee_breakdown',
        ],
        PLG_HIDDEN_RESTAURANT_DETAILS_FIELDS,
        hasPlgOnboarding,
      )}
      reservationMetrics={['companies_with_activated_reservations']}
    >
      {renderDateRangeFilter()}
      <Metrics />
    </StatisticsProvider>
  );
}
